/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react';
import { useAsync, useDebounce } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components/macro';

import { LitigationCausalResponse } from 'models/response';

import selectors from 'state/selectors';
import services from 'services';
import utils from 'utils';
import actions from 'state/actions';

import Header from 'components/Modal/Header';
import TextArea from 'components/Input/Textarea';
import CommonButton from 'components/Buttons/CommonButton';
import Dropdown from 'components/Input/dropdown/Dropdown';
import TextInput from 'components/Input/TextInput';
import Checkbox from 'components/Buttons/CheckboxButton';
import { RootState } from 'models';
import { actionType } from 'utils/constants';
import { useHistory } from 'react-router';
import { CloseLitigationBody } from 'models/request';

const TextAreaDiv = styled.div`
  min-width: 400px;
  max-width: 550px;
  max-height: 400px;
  width: 100%;
  overflow-y: scroll;
  min-height: 100px;
  border-radius: 5px;
  resize: none;
  border: 1px solid ${({ theme }) => theme.colorPalette.grey.grey5};
  padding-left: 5px;
  font-family: ${({ theme }) => theme.fontFamily};
  margin-bottom: 10px;
`;

const InputContainer = styled.div`
  display: flex;
  font-weight: ${({ theme }) => theme.fontWeightPalette.light};
  padding-bottom: 15px;
  padding-right: 15px;
  justify-content: flex-start;
  align-items: center;
  label {
    padding-right: 15px;
    font-size: ${({ theme }) => theme.fontSizePalette.xSmall};
    width: 150px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;

  > * {
    margin: 0 10px;
  }
`;

interface Option {
  label: string;
  value: number;
}
const emailPattern =
  // eslint-disable-next-line max-len
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const Litigation = () => {
  const modalProps = useSelector(selectors.modal.getModalProps);
  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();
  const protocols = useSelector(selectors.documents.selectAllActiveDocumentsID);
  const idCompany = useSelector(selectors.documents.selectAllActiveDocuments)[0]?.idCompany || -1;
  const records = useSelector(selectors.documents.selectAllActiveDocuments);
  const { val: DISABLE_LITIGATION_WITH_REGISTRATION } = useSelector((store: RootState) =>
    selectors.app.getConfig(store, actionType.DISABLE_LITIGATION_WITH_REGISTRATION),
  );
  const [note, setNote] = useState<string>('');
  const [causals, setCausals] = useState<LitigationCausalResponse['causalLitigationList']>([]);
  const [feedbackMail, setFeedbackMail] = useState<boolean>(false);
  const [mailValue, setMailValue] = useState<string>('');
  const [textOne, setTextOne] = useState<string>('');
  const [textTwo, setTextTwo] = useState<string>('');
  const [checkValue, setCheckValue] = useState<boolean>(false);
  const [selectedCausal, setSelectedCausal] = useState<Option | null>(null);
  const [mailCC, setMailCC] = useState<string>('');
  const [feedbackMailCC, setFeedbackMailCC] = useState<boolean>(false);
  const [showTemplateHTML, setShowTemplateHTML] = useState<boolean>(false);
  const history = useHistory();
  const [dirty, setDirty] = useState<boolean>(false);
  const [dirtyCC, setDirtyCC] = useState<boolean>(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const isTemplateEditable = causals.find((el) => el.idCausal === selectedCausal?.value)?.templateEditable;

  useAsync(async () => {
    try {
      const { data } = await services.openLitigation({
        protocols,
        idCompany,
      });
      const { causalLitigationList, mailSupplier } = data;

      setCausals(causalLitigationList);
      mailSupplier && setMailValue(mailSupplier);

      const defaultCausal = utils.input
        .buildOptions(causalLitigationList, 'causalName', 'idCausal')
        .find((option) => option.value === causalLitigationList.find((el) => el.defaultCausal)?.idCausal);

      if (defaultCausal) setSelectedCausal(defaultCausal);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const onSave = async () => {
    if (selectedCausal) {
      try {
        const params: CloseLitigationBody = {
          protocols,
          idCausal: selectedCausal.value,
          moveType: modalProps.type.toUpperCase(),
          mailAttach: modalProps.type === 'mail' ? checkValue : false,
          pdfAttach: modalProps.type === 'print' ? checkValue : false,
          idCompany,
          mailSupplier: mailValue || null,
          note,
          mailCc:
            mailCC
              .split(',')
              .map((email) => email.trim())
              .join(',') || null,
          park: !!modalProps.isLitigationPark,
        };

        if (modalProps.type === 'mail' && isTemplateEditable && editorRef.current) {
          params.mailBody = editorRef.current.innerHTML;
        }

        await services.closeLitigation(params);

        dispatch(actions.modal.closeModal());
        dispatch(actions.documents.removeRows(protocols));
        if (utils.documents.isOpenDocumentView()) dispatch(actions.documents.openNextDocument(history));
        utils.app.notify('success', t('success_litigation'));
      } catch (e) {
        console.error(e);
      }
    }
  };

  const onPrint = async () => {
    if (selectedCausal) {
      try {
        const { data } = await services.printPdfLitigationRequest({
          protocols,
          idCausal: selectedCausal.value,
          idCompany,
          text: textOne || null,
          text2: textTwo || null,
        });
        const date = new Date();
        utils.file.downloadFileFromBase64Source(
          `litigation-${date.toLocaleDateString()}`,
          data,
          'application/pdf',
          'pdf',
        );
        utils.app.notify('success', t('success_print'));
      } catch (e) {
        utils.app.notify('fail', t(`${e}`));
      }
    }
  };

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const onCausalChange = (opt: Option | null) => {
    opt && setSelectedCausal(opt);
  };

  useDebounce(
    () => {
      setFeedbackMail(!emailPattern.test(mailValue));
    },
    500,
    [mailValue],
  );

  useDebounce(
    () => {
      const emails = mailCC.split(',').map((email) => email.trim());
      setFeedbackMailCC(mailCC === '' ? false : emails.some((email) => !emailPattern.test(email)));
    },
    500,
    [mailCC],
  );

  useAsync(async () => {
    const idCausal = selectedCausal?.value;
    if (modalProps.type === 'mail' && protocols.length === 1 && idCausal && selectedCausal) {
      const { data } = await services.getLitigationMailTemplate(idCompany, idCausal, protocols[0]);
      setShowTemplateHTML(true);
      const attachmentMail = causals.find((el) => el.idCausal === selectedCausal.value)?.attachmentMail || false;
      setCheckValue(attachmentMail);

      if (editorRef.current) {
        editorRef.current.innerHTML = data;
      }
    }
  }, [modalProps.type, selectedCausal]);

  useEffect(() => {
    if (DISABLE_LITIGATION_WITH_REGISTRATION) {
      const find = records.find((el) => el.registrationProtocol !== null);
      if (find) {
        const lastCharacter = find.protocolIn[find.protocolIn.length - 1];
        if (lastCharacter === 'R' || lastCharacter === 'S') return;
        utils.app.notify('warning', t('protocol_already_registered'), 8000);
      }
    }
  }, [records, t, DISABLE_LITIGATION_WITH_REGISTRATION]);

  const checkDisabled = () => {
    if (!selectedCausal) return true;
    if (modalProps.type === 'mail') return feedbackMail;

    if (DISABLE_LITIGATION_WITH_REGISTRATION) {
      const find = records.find((el) => el.registrationProtocol !== null);

      if (find) {
        const lastCharacter = find.protocolIn[find.protocolIn.length - 1];
        if (lastCharacter === 'R' || lastCharacter === 'S') return false;
        return true;
      }
    }

    return false;
  };

  const getTitleAndSubtitle = (
    type: 'mail' | 'print' | 'archive',
    isLitigationPark: boolean,
    t: (label: string) => string,
  ) => {
    const typeMap = {
      mail: 'litigation-mail',
      print: 'litigation-print',
      archive: 'litigation-archive',
    };

    const titleKey = isLitigationPark ? `litigation_park_${type}` : typeMap[type];

    const subtitleKey = `subtitle-for-${titleKey}`;

    return {
      title: t(titleKey),
      subtitle: t(subtitleKey),
    };
  };

  const { title, subtitle } = getTitleAndSubtitle(modalProps.type, modalProps.isLitigationPark, t);

  return (
    <>
      <Header title={title} subtitle={subtitle} />
      <InputContainer>
        <label>{t('causal')}</label>
        <Dropdown
          id="causal-dropdown"
          value={selectedCausal}
          onChange={onCausalChange}
          options={utils.input.buildOptions(causals, 'causalName', 'idCausal')}
        />
      </InputContainer>
      {modalProps.type === 'print' ? (
        <InputContainer>
          <label>{t('text_one')}</label>
          <TextInput
            id="litigation-text-one"
            value={textOne}
            onChange={(e: React.FocusEvent<HTMLInputElement>) => {
              setTextOne(e.target.value);
            }}
          />
        </InputContainer>
      ) : null}
      {modalProps.type === 'print' ? (
        <InputContainer>
          <label>{t('text_two')}</label>
          <TextInput
            id="litigation-text-two"
            value={textTwo}
            onChange={(e: React.FocusEvent<HTMLInputElement>) => {
              setTextTwo(e.target.value);
            }}
          />
        </InputContainer>
      ) : null}
      {modalProps.type === 'mail' ? (
        <>
          <InputContainer>
            <label>{t('supplier_mail')}</label>
            <TextInput
              id="litigation-mail"
              value={mailValue}
              onBlur={() => setDirty(true)}
              hasFeedback={dirty && feedbackMail}
              feedbackMessage={mailValue === '' ? t('mail-required') : t('mail_not_valid')}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                setDirty(false);
                setMailValue(e.target.value);
              }}
            />
          </InputContainer>
          <InputContainer>
            <label>{t('mail_cc')}</label>
            <TextInput
              id="litigation-mail-cc"
              value={mailCC}
              hasFeedback={dirtyCC && feedbackMailCC}
              onBlur={() => setDirtyCC(true)}
              feedbackMessage={t('mails_not_valid')}
              onChange={(e: React.FocusEvent<HTMLInputElement>) => {
                setDirtyCC(false);
                setMailCC(e.target.value);
              }}
            />
          </InputContainer>
        </>
      ) : null}
      {modalProps.type === 'mail' || modalProps.type === 'print' ? (
        <InputContainer>
          <label>{t(`attachment-${modalProps.type}`)}</label>
          <Checkbox isActive={checkValue} action={(value: boolean) => setCheckValue(value)} />
        </InputContainer>
      ) : null}
      {showTemplateHTML ? (
        <TextAreaDiv ref={editorRef} contentEditable={modalProps.type === 'mail' && isTemplateEditable} />
      ) : null}
      {modalProps.type === 'mail' ? (
        !isTemplateEditable ? (
          <TextArea data-walkthrough="litigation-note" onChange={onNoteChange} value={note} />
        ) : null
      ) : (
        <TextArea data-walkthrough="litigation-note" onChange={onNoteChange} value={note} />
      )}
      <ButtonsContainer>
        <CommonButton
          id="litigation-confirm-btn"
          type="submit"
          disabled={checkDisabled()}
          scope="primary"
          value={t('confirm')}
          action={onSave}
        />
        {modalProps.type === 'print' ? (
          <CommonButton
            id="litigation-print-btn"
            type="button"
            scope="primary"
            value={t('print')}
            disabled={!selectedCausal}
            action={onPrint}
          />
        ) : null}
        <CommonButton
          id="litigation-cancel-btn"
          scope="secondary"
          value={t('cancel')}
          action={() => dispatch(actions.modal.closeModal())}
        />
      </ButtonsContainer>
    </>
  );
};

export default Litigation;
